/**
 * PublicShopPage — displays a public shop's details without requiring auth.
 * Route: /shop/:shopId
 */
import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiUrl } from '../../config';
import './PublicShopPage.css';

interface PublicShop {
  id: string;
  name: string;
  address: string;
  category: string;
  sections: { sectionId: string; name: string; order: number }[];
  createdAt: string;
}

export default function PublicShopPage() {
  const { shopId } = useParams<{ shopId: string }>();
  const [shop, setShop] = useState<PublicShop | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!shopId) return;
    setLoading(true);
    fetch(apiUrl(`/api/shops/public/${shopId}`))
      .then((res) => {
        if (!res.ok) throw new Error(res.status === 404 ? 'Shop not found' : `Error ${res.status}`);
        return res.json();
      })
      .then((data) => {
        setShop(data);
        setError(null);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [shopId]);

  if (loading) {
    return (
      <div className="public-shop-page">
        <div className="public-shop-loading">Loading shop...</div>
      </div>
    );
  }

  if (error || !shop) {
    return (
      <div className="public-shop-page">
        <div className="public-shop-error">
          <h2>Shop Not Found</h2>
          <p>{error || 'This shop does not exist or is not public.'}</p>
          <Link to="/login" className="public-shop-cta">Sign in to Dukkan</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="public-shop-page">
      <div className="public-shop-card">
        <h1 className="public-shop-name">{shop.name}</h1>
        {shop.category && <span className="public-shop-category">{shop.category}</span>}
        {shop.address && <p className="public-shop-address">{shop.address}</p>}

        {shop.sections.length > 0 && (
          <div className="public-shop-sections">
            <h3>Sections</h3>
            <ul>
              {shop.sections
                .sort((a, b) => a.order - b.order)
                .map((sec) => (
                  <li key={sec.sectionId}>{sec.name}</li>
                ))}
            </ul>
          </div>
        )}

        <div className="public-shop-footer">
          <p>Powered by <Link to="/login">Dukkan</Link></p>
        </div>
      </div>
    </div>
  );
}
